import { useTranslation } from "react-i18next";
import { UseFormRegister, UseFormGetValues, UseFormSetValue, Controller, Control } from "react-hook-form";
import { DocumentReferenceResponse, GetExtraWorkAgreementQueryResponse, useGetApiConfigQuery } from "api/generatedApi";
import { ExtraWorkAgreementFormData } from "../hooks/use-input-validation";
import { NoteField } from "shared/editors/note-field";
import { Box } from "@mui/material";
import { UploadField } from "shared/editors/upload-field";
import { useScreenSize } from "shared/use-screen-size";
import { useEffect, useState } from "react";
import { useToast } from "shared/toast/hooks/use-toast";

const MaxExtraWorkAgreementLength = 1000;

interface Props {
  agreement?: GetExtraWorkAgreementQueryResponse;
  disabled: boolean;
  register: UseFormRegister<ExtraWorkAgreementFormData>;
  getValue: UseFormGetValues<ExtraWorkAgreementFormData>;
  setValue: UseFormSetValue<ExtraWorkAgreementFormData>;
  control: Control<ExtraWorkAgreementFormData>;
  setTempFilesForDelete: React.Dispatch<React.SetStateAction<DocumentReferenceResponse[]>>;
  setTempFilesforUpload: React.Dispatch<React.SetStateAction<File[]>>;
  canEdit: boolean;
  documents: DocumentReferenceResponse[];
  resetLocalDocumentSwitch?: boolean;
}

export const EditExtraWorkAgreementDocumentation = (props: Props) => {
  const { disabled, setTempFilesForDelete, setTempFilesforUpload, canEdit } = props;
  const { t } = useTranslation();
  const { screenSize } = useScreenSize();
  const [localDocuments, setLocalDocuments] = useState<DocumentReferenceResponse[]>(props.documents ? [...props.documents] : []);
  const { data } = useGetApiConfigQuery();
  const toast = useToast();

  useEffect(() => {
    setLocalDocuments((prev) => {
      // Keep temp documents (still uploading)
      const tempDocs = prev.filter((doc) => doc.documentId?.startsWith("temp-"));

      // Remove temp docs that now exist as real docs in props.documents (upload completed)

      const activeTempDocs = tempDocs.filter((tempDoc) => !props.documents.some((realDoc) => realDoc.name === tempDoc.name));

      // Combine: all real documents from server + active temp documents
      const result = [...props.documents, ...activeTempDocs];
      return result;
    });
  }, [props.documents]);

  useEffect(() => {
    setLocalDocuments(props.documents ?? []);
  }, [props.resetLocalDocumentSwitch]);

  const canEditMethod = () => {
    return canEdit;
  };

  const canDelete = () => {
    if (disabled) {
      return false;
    }
    return canEdit;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // get the file from the event
    const target = event.target;
    const fileObj = target?.files && target.files[0];

    if (fileObj && data?.maxUploadFileSizeMB && fileObj?.size > data?.maxUploadFileSizeMB * 1024 * 1024) {
      // If the file is larger than the max upload size, show an error message
      toast.error(t("content.overview.documents.uploadFileSizeError", { maxFileSize: data?.maxUploadFileSizeMB }));
      return;
    }

    if (fileObj) {
      //get the file name
      const baseName = fileObj.name;
      //find the file extension
      const extMatch = baseName.match(/(\.[^\.]+)$/);
      // If no extension is found, use an empty string
      const ext = extMatch ? extMatch[0] : "";
      // Remove the extension from the base name
      const nameWithoutExt = baseName.replace(/(\.[^\.]+)$/, "");
      // get the base name in a let
      let uniqueName = baseName;
      //number to append the file name with if it exists
      let counter = 0;
      // Check if the file name already exists in localDocuments
      // If it does, use a loop to append a number to the file name until it is unique
      let exists = localDocuments.some((doc) => typeof doc.name === "string" && doc.name === uniqueName);
      while (exists) {
        counter++;
        uniqueName = `${nameWithoutExt} (${counter})${ext}`;
        exists = localDocuments.some((doc) => typeof doc.name === "string" && doc.name === uniqueName);
      }
      // Create a new File object with the unique name but the same content as the original file
      // This is necessary because the File constructor does not allow changing the name of an existing file
      const renamedFile = new File([fileObj], uniqueName, { type: fileObj.type });

      // Update localDocuments with the new file for the visual representation
      setLocalDocuments((prev) => [
        ...prev,
        {
          name: renamedFile.name,
          documentId: `temp-${renamedFile.name}-${Date.now()}`,
          size: renamedFile.size,
          uploadedTimestamp: new Date().toISOString(),
        },
      ]);
      // Add the new file to the tempFilesforUpload state
      setTempFilesforUpload((prev) => [...prev, renamedFile]);
    }

    if (event.target) {
      event.target.value = ""; // allows for re-uploading the same file
    }
  };

  const handleDeleteDocument = (document: DocumentReferenceResponse) => {
    // a temporary document is created with a id starting with "temp-" so we can identify it
    if (!document.documentId?.startsWith("temp-")) {
      setTempFilesForDelete((prev) => [...prev, document]);
    }
    // Remove the document from localDocuments
    // This is necessary to update the UI immediately without waiting for the API response
    setLocalDocuments((prev) => prev.filter((doc) => doc.documentId !== document.documentId));

    //remove from tempFilesforUpload if the name matches. This has a vulnerability if the name is not unique
    setTempFilesforUpload((prev) => prev.filter((file) => file.name !== document.name));
  };

  return (
    <Box>
      <Controller
        name="description"
        control={props.control}
        render={({ field }) => (
          <NoteField
            label={disabled ? t("dashboard.extraWorkAgreements.create.noteTitleView") : t("dashboard.extraWorkAgreements.create.noteTitleEdit")}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            screenSize={screenSize}
            disableButton={true}
            canEditNote={canEdit}
            disableEdit={disabled}
            disablePadding={true}
            labelVariant="caption"
            labelWeight="normal"
            textColor="black"
            minHeight="0px"
            borderRadius="0px"
            noContentText={t("dashboard.extraWorkAgreements.create.notePlaceholder")}
            inputProps={{
              maxLength: MaxExtraWorkAgreementLength,
            }}
            lineClamp={100}
          />
        )}
      />

      <Box sx={{ height: "20px" }} />

      <UploadField
        textColor="primary.black"
        allowDownloads={true}
        isUploading={false}
        noContentText={t("dashboard.extraWorkAgreements.create.docPlaceholder")}
        title={disabled ? t("dashboard.extraWorkAgreements.create.docTitleView") : t("dashboard.extraWorkAgreements.create.docTitleEdit")}
        canUploadDocument={canEditMethod}
        documents={localDocuments}
        handleFileChange={handleFileChange}
        screenSize={useScreenSize().screenSize}
        inADialog={true}
        canDeleteDocument={canDelete}
        clickDocumentDelete={handleDeleteDocument}
        disabled={disabled}
        disablePadding={true}
        titelVariant="caption"
        titelWeight="normal"
        borderRadius="0px"
        allowDownloadWhileInDialog={true}
      />
    </Box>
  );
};
